import { useEffect, useState } from 'react';
import AdminSidebar from '../components/AdminSidebar';

const API_BASE = process.env.REACT_APP_API_BASE_URL || `${process.env.FRONTEND_URL}/api`;

const HERO_STAT_FIELDS = [
  { key: 'hero_stat_tuition', label: 'Tuition Stat', placeholder: 'e.g. $600+ Tuition/Sem' },
  { key: 'hero_stat_visa_assistance', label: 'Visa Assistance Stat', placeholder: 'e.g. 100% Visa Assistance' },
  { key: 'hero_stat_students_placed', label: 'Students Placed Stat', placeholder: 'e.g. 2,500+ Students Placed' },
];

export default function AdminSettingsPage() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  const token = localStorage.getItem('adminToken');

  const fetchSettings = () => {
    setLoading(true);
    fetch(`${API_BASE}/admin/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((json) => {
        if (json.success) {
          const map = {};
          json.data.forEach((s) => { map[s.key] = s.value; });
          setSettings(map);
        }
      })
      .finally(() => setLoading(false));
  };

  useEffect(fetchSettings, []);

  const updateSetting = async (key, value) => {
    setSaving(true);
    setMessage('');
    await fetch(`${API_BASE}/admin/settings/${key}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ value }),
    });
    setSettings((prev) => ({ ...prev, [key]: value }));
    setSaving(false);
    setMessage('Saved.');
    setTimeout(() => setMessage(''), 2000);
  };

  const togglePipelineDefault = () => {
    const current = settings.default_pipeline_visibility === 'true';
    updateSetting('default_pipeline_visibility', String(!current));
  };

  if (loading) return <p className="text-center py-40 text-[#64748b]">Loading settings...</p>;

  return (
    <div className="bg-[#f7f9fb] font-['Plus_Jakarta_Sans'] min-h-screen">
      <AdminSidebar />
      <main className="ml-64 min-h-screen p-8">
        <div className="max-w-3xl">
          <h2 className="text-3xl font-bold text-[#00255a] mb-2">Settings</h2>
          <p className="text-[#64748b] mb-8">Global platform configuration.</p>

          {message && (
            <div className="mb-6 p-3 bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg">{message}</div>
          )}

          {/* Pipeline Visibility Default */}
          <section className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm p-8 mb-8">
            <h3 className="text-lg font-bold text-[#00255a] mb-2">Default Pipeline Visibility</h3>
            <p className="text-[#64748b] mb-6 text-sm">
              When enabled, newly invited agents see the full applications pipeline by default instead of
              only their own assigned cases. This can still be overridden per-agent in Agent Management.
            </p>
            <div className="flex items-center justify-between p-4 bg-[#f7f9fb] rounded-xl border border-[#e2e8f0]">
              <div>
                <p className="font-semibold text-[#0f172a]">
                  {settings.default_pipeline_visibility === 'true' ? 'Enabled' : 'Disabled'}
                </p>
                <p className="text-xs text-[#64748b]">Applies to new agents going forward — not retroactive.</p>
              </div>
              <button
                onClick={togglePipelineDefault}
                disabled={saving}
                className={`w-14 h-7 rounded-full relative transition-colors ${
                  settings.default_pipeline_visibility === 'true' ? 'bg-[#00255a]' : 'bg-[#e0e3e5]'
                }`}
              >
                <div
                  className={`w-5 h-5 bg-white rounded-full absolute top-1 transition-transform ${
                    settings.default_pipeline_visibility === 'true' ? 'translate-x-8' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </section>

          {/* Hero Stats */}
          <section className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm p-8 mb-8">
            <h3 className="text-lg font-bold text-[#00255a] mb-2">Vision Statement</h3>
            <p className="text-[#64748b] mb-6 text-sm">
              Short mission/vision copy shown on the homepage, explaining what IASC does.
            </p>
            <VisionStatementField
              value={settings.vision_statement || ''}
              onSave={(v) => updateSetting('vision_statement', v)}
              saving={saving}
            />
          </section>

          <section className="bg-white rounded-2xl border border-[#e2e8f0] shadow-sm p-8">
            <h3 className="text-lg font-bold text-[#00255a] mb-2">Homepage Hero Stats</h3>
            <p className="text-[#64748b] mb-6 text-sm">
              The three stat callouts shown on the homepage hero section (e.g. tuition from, visa assistance, students placed).
            </p>
            <div className="space-y-6">
              {HERO_STAT_FIELDS.map((field) => (
                <HeroStatField
                  key={field.key}
                  field={field}
                  value={settings[field.key] || ''}
                  onSave={(v) => updateSetting(field.key, v)}
                  saving={saving}
                />
              ))}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}

function VisionStatementField({ value, onSave, saving }) {
  const [localValue, setLocalValue] = useState(value);
  useEffect(() => setLocalValue(value), [value]);

  return (
    <div>
      <textarea
        rows="4"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        placeholder="e.g. IASC Consultancy exists to make international education and career mobility accessible to every ambitious student and professional..."
        className="w-full px-4 py-3 rounded-xl border border-[#c4c6d1] outline-none focus:ring-2 focus:ring-[#00255a]/10 resize-none mb-3"
      />
      <button
        onClick={() => onSave(localValue)}
        disabled={saving || localValue === value}
        className="px-6 py-3 bg-[#00255a] text-white rounded-xl font-semibold text-sm disabled:opacity-50"
      >
        Save
      </button>
    </div>
  );
}

function HeroStatField({ field, value, onSave, saving }) {
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => setLocalValue(value), [value]);

  return (
    <div className="flex items-end gap-3">
      <div className="flex-1">
        <label className="block text-xs font-bold text-[#44474f] uppercase mb-2">{field.label}</label>
        <input
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          placeholder={field.placeholder}
          className="w-full px-4 py-3 rounded-xl border border-[#c4c6d1] outline-none focus:ring-2 focus:ring-[#00255a]/10"
        />
      </div>
      <button
        onClick={() => onSave(localValue)}
        disabled={saving || localValue === value}
        className="px-6 py-3 bg-[#00255a] text-white rounded-xl font-semibold text-sm disabled:opacity-50"
      >
        Save
      </button>
    </div>
  );
}